import React from 'react';
import PropTypes from 'prop-types';

import camelcaseKeys from 'camelcase-keys';
import classNames from 'classnames';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMinus, faPlus, faBuilding } from '@fortawesome/pro-solid-svg-icons';

import listingPropTypes from './prop_types';
import PropertyTypeInputs from 'components/property_type_inputs';
import ResearchPropertyAsyncSelect from 'components/research_property/async_select';
import StateSelect from 'components/state_select';
import ToggleControl from 'components/toggle_control';
import ReadOnlyField from 'components/read_only_field';
import * as AddressFormatter from 'helpers/formatters/address_formats';
import './selected_property.scss';

export default class ListingSelectedProperty extends React.Component {
  static propTypes = {
    availableDatabases: PropTypes.array,
    controlWrapper: PropTypes.any,
    disabledForAssignment: PropTypes.bool,
    features: PropTypes.shape({
      crm: PropTypes.bool,
    }),
    formDescription: PropTypes.node,
    includeEmpty: PropTypes.bool,
    isAssignment: PropTypes.bool,
    listing: listingPropTypes.isRequired,
    newStyles: PropTypes.bool,
    propertyTypes: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.number.isRequired,
      subtypes: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.number.isRequired,
      }))
    })),
    readOnly: PropTypes.bool,
    searchDescription: PropTypes.node,
    onChange: PropTypes.func.isRequired,
    onFormToggle: PropTypes.func,
  }

  static defaultProps = {
    formDescription: 'Or, create a new one:',
    searchDescription: 'Select a property from your database:',
    onFormToggle: () => {},
  }

  propertyRequired = (props = this.props) => {
    return props.isAssignment && !props.disabledForAssignment;
  }

  state = {
    showPropertyForm: (
      this.propertyRequired() ||
      (this.props.listing.selectedProperty && this.props.listing.selectedProperty.id)
    )
  }

  componentDidMount() {
    this.props.onFormToggle(this.state.showPropertyForm);
  }

  componentDidUpdate(previousProps, prevState) {
    if (
      !this.propertyRequired() && prevState.showPropertyForm && this.state.showPropertyForm &&
      previousProps.listing.selectedProperty && this.props.listing.selectedProperty === null
    ) {
      this.setState({ showPropertyForm: false });
    }

    if (
      !prevState.showPropertyForm && !this.state.showPropertyForm &&
      (
        (
          previousProps.listing.selectedProperty &&
          previousProps.listing.selectedProperty.id === null &&
          this.props.listing.selectedProperty &&
          this.props.listing.selectedProperty.id
        ) ||
        (!this.propertyRequired(previousProps) && this.propertyRequired())
      )
    ) {
      this.setState({ showPropertyForm: true });
    }

    if (this.state.showPropertyForm !== prevState.showPropertyForm) {
      this.props.onFormToggle(this.state.showPropertyForm);
    }
  }

  get availableDatabases() {
    return this.props.availableDatabases || [];
  }

  get crmEnabled() {
    return !this.props.features || this.props.features.crm;
  }

  get defaultDatabaseId() {
    if (!this.crmEnabled && this.availableDatabases.length) {
      return this.availableDatabases[0][1];
    }
  }

  handleSelectedPropertyChange = (event) => {
    const { name, value } = event.target;

    const selectedProperty = {
      ...this.props.listing.selectedProperty,
      [name]: value,
    };

    this.setDefaultPropertyTypeIds(selectedProperty);
    this.props.onChange({ selectedProperty });
  }

  handlePropertyTypeChange = (changes) => {
    this.props.onChange({
      selectedProperty: {
        ...this.props.listing.selectedProperty,
        ...changes
      }
    });
  }

  handleSelectedPropertySelect = (selectedProperty) => {
    // TODO: Fix the underlying component's response handling instead
    this.props.onChange({
      selectedProperty: {
        ...camelcaseKeys(selectedProperty),
        ...selectedProperty
      }
    });
  }

  handleListingChange = (event) => {
    const { name, value } = event.target;
    this.props.onChange({ [name]: value });
  }

  toggleSelectedProperty = () => {
    const previousShowPropertyForm = this.state.showPropertyForm;
    this.setState(({ showPropertyForm }) => ({ showPropertyForm: this.propertyRequired() || !showPropertyForm }),
      () => {
        if (this.props.isAssignment || previousShowPropertyForm) {
          this.props.onChange({ selectedProperty: { id: null } });
        } else {
          const { id, databaseId, ...rest } = this.props.listing.selectedProperty || {};
          const selectedProperty = {
            ...rest,
            databaseId: databaseId || this.defaultDatabaseId
          };
          this.setDefaultPropertyTypeIds(selectedProperty);
          this.props.onChange({ selectedProperty });
        }
      });
  }

  setDefaultPropertyTypeIds(selectedProperty) {
    if (selectedProperty.propertyTypeId == null && this.props.propertyTypes && this.props.propertyTypes.length > 0) {
      const propertyType = this.props.propertyTypes[0];
      selectedProperty.propertyTypeId = propertyType.id;
      if (propertyType.subtypes.length > 0) {
        selectedProperty.propertySubtypeId = propertyType.subtypes[0].id;
      }
    }
  }

  render() {
    const { disabledForAssignment, includeEmpty } = this.props;
    const { showPropertyForm } = this.state;
    const hasProperty = !includeEmpty || showPropertyForm;

    return (
      <div className={classNames('selected-property', { 'no-selection': !hasProperty || disabledForAssignment })}>
        {(!hasProperty || disabledForAssignment) && (
          <React.Fragment>
            <FontAwesomeIcon className="text-muted" icon={faBuilding} />
            <div className="mt-3 mb-2">No property information has been added to the voucher</div>
          </React.Fragment>
        )}

        {!disabledForAssignment ? this.renderToggleLink() : (
          <div name="disabled-assignment-message">
            Property information can be added when the assignment status is
            <div>
              <strong>Under Contract</strong> or <strong>Closed</strong>
            </div>
          </div>
        )}

        {showPropertyForm && !disabledForAssignment && this.renderSelectedPropertyForm()}
      </div>
    );
  }

  renderToggleLink() {
    const { listing, newStyles } = this.props;
    const { showPropertyForm } = this.state;

    if (this.propertyRequired() && (!listing.selectedProperty || !listing.selectedProperty.id)) {
      return;
    }

    if (newStyles) {
      const ControlWrapper = this.props.controlWrapper;

      const controlProps = {
        className: 'px-4',
        children: `${showPropertyForm ? 'Remove' : 'Add'} Property Information`,
        isToggledOn: !!showPropertyForm,
      };

      const wrapperProps = {
        name: 'toggle-property',
        onClick: this.toggleSelectedProperty,
      };

      if (ControlWrapper) {
        return (
          <ControlWrapper {...wrapperProps}>
            <ToggleControl {...controlProps} />
          </ControlWrapper>
        );
      } else {
        return <ToggleControl {...controlProps} {...wrapperProps} />;
      }
    }

    return (
      <a className="btn btn-link p-0" name="toggle-property" onClick={this.toggleSelectedProperty}>
        <h4>
          <FontAwesomeIcon className="mr-2" icon={showPropertyForm ? faMinus : faPlus} />
          {showPropertyForm ? 'Remove' : 'Add'} Property Details
        </h4>
      </a>
    );
  }

  renderSelectedPropertyForm() {
    const { listing } = this.props;
    const selectedProperty = listing.selectedProperty || {};

    return (
      <div className="form form-horizontal">
        {this.renderFormHeader()}

        {this.crmEnabled && !selectedProperty.id && this.availableDatabases.length > 0 && (
          <div className="form-group">
            <label className="control-label">Database</label>
            <div className="form-input-container">
              <select
                className="form-control"
                name="databaseId"
                value={selectedProperty.databaseId || ''}
                onChange={this.handleSelectedPropertyChange}
              >
                <option>Select a database..</option>
                {this.availableDatabases.map(db => <option key={db[1]} value={db[1]}>{db[0]}</option>)}
              </select>
            </div>
          </div>
        )}

        {this.renderSelectedPropertyInput('Property Name', 'name', { fromDatabase: true, required: false })}
        {this.renderAddress()}
        {this.renderSelectedPropertyInput('County', 'county', { fromDatabase: true, required: false })}
        {this.renderPropertyTypeInputs()}
        {this.renderSizeInput()}

        {this.props.listing.leaseClosed && (
          <div className="form-group">
            <label className="control-label">Lease Space</label>
            <div className="form-input-container">
              <input
                className="form-control"
                name="closingLeaseSpace"
                value={this.props.listing.closingLeaseSpace || ''}
                onChange={this.handleListingChange}
              />
            </div>
          </div>
        )}
      </div>
    );
  }

  renderAddress() {
    const selectedProperty = this.props.listing.selectedProperty || {};
    const readOnly = selectedProperty.id && this.props.readOnly;

    if (readOnly) {
      return (
        <ReadOnlyField
          label="Property Address"
          value={AddressFormatter.oneLine(selectedProperty, { addressDelimiter: ' | ' })}
        />
      );
    }

    return <React.Fragment>
      {this.renderSelectedPropertyInput('Address', 'address', { fromDatabase: true })}
      {this.renderSelectedPropertyInput('City', 'city', { fromDatabase: true })}
      <div className="form-group required">
        <label className="control-label">State</label>
        <div className="form-input-container">
          <StateSelect
            className="form-control"
            disabled={!!selectedProperty.id}
            name="state"
            value={selectedProperty.state}
            onChange={this.handleSelectedPropertyChange}
          />
        </div>
      </div>
      {this.renderSelectedPropertyInput('Zip', 'zip', { fromDatabase: true })}
    </React.Fragment>;
  }

  renderFormHeader() {
    if (this.crmEnabled) {
      return <React.Fragment>
        <div className="mb-2">{this.props.searchDescription}</div>
        <ResearchPropertyAsyncSelect
          researchProperty={this.props.listing.selectedProperty || {}}
          onSelect={this.handleSelectedPropertySelect}
        />
        <div className="mt-4 mb-2">{this.props.formDescription}</div>
      </React.Fragment>;
    }
  }

  renderPropertyTypeInputs() {
    if (this.props.propertyTypes) {
      const selectedProperty = this.props.listing.selectedProperty || {};

      return (
        <PropertyTypeInputs
          disabled={!!selectedProperty.id}
          includeBlank={!!selectedProperty.id}
          propertySubtypeId={selectedProperty.propertySubtypeId}
          propertyTypeId={selectedProperty.propertyTypeId}
          propertyTypes={this.props.propertyTypes}
          readOnly={!!selectedProperty.id && this.props.readOnly}
          required
          onChange={this.handlePropertyTypeChange}
        />
      );
    }
  }

  renderSizeInput() {
    const { propertyTypeId } = this.props.listing.selectedProperty || {};

    if (propertyTypeId === 5) {
      return this.renderSelectedPropertyInput('Lot Size (Acres)', 'lotSize', {
        fromDatabase: true,
        readOnlyLabel: 'Lot Size',
        readOnlySuffix: 'Acres',
        required: false,
      });
    } else {
      return this.renderSelectedPropertyInput('Building Size (SF)', 'buildingSize', {
        fromDatabase: true,
        readOnlyLabel: 'Building Size',
        readOnlySuffix: 'SF',
        required: false,
      });
    }
  }

  renderSelectedPropertyInput(label, name, { fromDatabase, readOnlyLabel, readOnlySuffix, required = true } = {}) {
    const selectedProperty = this.props.listing.selectedProperty || {};
    const readOnlyStandalone = selectedProperty.id && fromDatabase && this.props.readOnly;
    if (readOnlyStandalone) {
      let value = '';
      if (selectedProperty[name]) {
        value = selectedProperty[name];

        if (readOnlySuffix) {
          value = `${value} ${readOnlySuffix}`;
        }
      }

      return <ReadOnlyField label={readOnlyLabel || label} value={value} />;
    }

    return (
      <div className={classNames('form-group', { required })}>
        <label className="control-label">{label}</label>
        <div className="form-input-container">
          <input
            className="form-control"
            disabled={selectedProperty.id && fromDatabase}
            name={name}
            value={selectedProperty[name] || ''}
            onChange={this.handleSelectedPropertyChange}
          />
        </div>
      </div>
    );
  }
}
